import { Observable } from 'rxjs';

import {
  AcquisitionChannel,
  AcquisitionChannelsResponse,
  CreateAcquisitionChannelPayload,
  PatchAcquisitionChannelPayload,
} from '../../../interfaces/acquisition.interface';
import { AuditComment, AuditCommentsResponse } from '../../../interfaces/audit-comment.interface';
import {
  BlockedEmail,
  BlockedEmailsResponse,
  CreateBlockedEmailPayload,
  GetBlockedEmailsParams,
  PatchBlockedEmailPayload,
} from '../../../interfaces/blocked-email.interface';
import {
  AddLeadAcquisitionCostPayload,
  CreateLeadPayload,
  GetLeadsParams,
  Lead,
  LeadAcquisitionCost,
  LeadAcquisitionCosts,
  LeadCaptureSettings,
  LeadCostType,
  LeadCostTypesResponse,
  LeadDuplicateDecision,
  LeadDuplicatesSettings,
  LeadOptions,
  LeadsResponse,
  PatchLeadCostTypePayload,
  PatchLeadPayload,
} from '../../../interfaces/lead.interface';
import { EntityTag } from '../../../interfaces/tag.interface';

/** Único punto de acceso a datos de leads (`/leads`). Clase abstracta: es el token de inyección. */
export abstract class LeadsApiService {
  abstract list(filters: GetLeadsParams): Observable<LeadsResponse>;

  abstract get(id: number): Observable<Lead>;

  abstract create(body: CreateLeadPayload): Observable<Lead>;

  abstract patch(id: number, body: PatchLeadPayload): Observable<Lead>;

  /** Deja exactamente esas etiquetas; requiere `manage_leads`. */
  abstract setTags(id: number, tagIds: number[]): Observable<{ tags: EntityTag[] }>;

  /** Canales, fuentes y estatus manuales para filtros y formularios. */
  abstract options(): Observable<LeadOptions>;

  abstract getCaptureSettings(): Observable<LeadCaptureSettings>;

  abstract updateCaptureSettings(body: LeadCaptureSettings): Observable<LeadCaptureSettings>;

  /** Confirma o descarta un candidato a duplicado; responde el lead actualizado. */
  abstract decideDuplicate(
    leadId: number,
    candidateId: number,
    decision: LeadDuplicateDecision,
  ): Observable<Lead>;

  abstract getDuplicatesSettings(): Observable<LeadDuplicatesSettings>;

  abstract updateDuplicatesSettings(
    body: LeadDuplicatesSettings,
  ): Observable<LeadDuplicatesSettings>;

  /** Lista de correos y dominios que el alta de leads rechaza (`/leads/blocked-emails`). */
  abstract listBlockedEmails(filters: GetBlockedEmailsParams): Observable<BlockedEmailsResponse>;

  abstract createBlockedEmail(body: CreateBlockedEmailPayload): Observable<BlockedEmail>;

  abstract updateBlockedEmail(id: number, body: PatchBlockedEmailPayload): Observable<BlockedEmail>;

  /** Del más nuevo al más viejo; `before` es el `nextBefore` de la página previa. */
  abstract listComments(id: number, before?: number): Observable<AuditCommentsResponse>;

  abstract addComment(id: number, comment: string): Observable<AuditComment>;

  /** Total y registros del costo de adquisición de un lead. */
  abstract getAcquisitionCosts(id: number): Observable<LeadAcquisitionCosts>;

  abstract addAcquisitionCost(
    id: number,
    body: AddLeadAcquisitionCostPayload,
  ): Observable<LeadAcquisitionCost>;

  /** Crea el reverso del registro; el motivo es obligatorio. */
  abstract reverseAcquisitionCost(
    id: number,
    costId: number,
    notes: string,
  ): Observable<LeadAcquisitionCost>;

  /** Catálogo completo de tipos de costo, activos o no (`/leads/cost-types`). */
  abstract listCostTypes(): Observable<LeadCostTypesResponse>;

  abstract updateCostType(code: string, body: PatchLeadCostTypePayload): Observable<LeadCostType>;

  /** Canales de adquisición, activos o no (`/leads/channels`). El código no cambia ni se borra. */
  abstract listChannels(): Observable<AcquisitionChannelsResponse>;

  abstract createChannel(body: CreateAcquisitionChannelPayload): Observable<AcquisitionChannel>;

  /** 409 `ACQUISITION_CHANNEL_IN_USE` al desactivar un canal que usa un proveedor. */
  abstract updateChannel(
    code: string,
    body: PatchAcquisitionChannelPayload,
  ): Observable<AcquisitionChannel>;
}
